<template>
  <LoadingComponent
    v-if="!localDashboard || localDashboard.id !== currentBoard.id"
  />
  <template v-if="localDashboard">
    <div class="flex-shrink-0">
      <DashboardHeader :dashboard="localDashboard">
        <template #cta>
          <DashboardPanelControl
            v-model:dashboard="localDashboard"
            v-model:need-save-locked="loading"
            v-model:require-save-dashboard="requireSaveDashboard"
            :count-filter="validFilter.length ?? 0"
          >
            <template #filter-content="{ close }">
              <FilterLinesComponent
                v-model:filters="localDashboard.filters"
                v-model:require-save-dashboard="requireSaveDashboard"
                :filters-available="availableFilter"
                @close-popover="() => closePopover(close)"
              />
            </template>
          </DashboardPanelControl>
        </template>
      </DashboardHeader>
      <div
        v-if="localDashboard.filters?.length"
        class="mt-2 flex flex-1 rounded-md border p-2"
      >
        <p class="capitalize-first underline">
          {{ $t('global.advanced_filters') }} :
        </p>
        <div v-for="(filter, index) in renderedFilters" :key="index">
          <span v-html="filter"></span>
          <span>{{
            filter.condition === '!=' ? $t('global.not_included') : ' '
          }}</span>
        </div>
      </div>
    </div>

    <div class="flex min-h-0 flex-1 flex-col gap-4 overflow-scroll">
      <div
        v-for="(row, index_analytic_rows) in localDashboard?.analytic_rows"
        :key="row?.id"
        class="grid gap-4"
        :class="[
          row?.type === 'simple'
            ? 'grid-cols-3 md:grid-cols-6'
            : 'grid-cols-2 md:grid-cols-4'
        ]"
      >
        <template v-if="row?.type === 'simple'">
          <template v-if="row?.elements?.length === 0">
            <div
              class="group relative flex h-20 flex-col rounded-md border p-2 pt-1"
            >
              <button
                class="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 text-sm font-medium"
                @click="() => add(row.id)"
              >
                <span class="">
                  {{ $t('global.click_hier_to_set_up_your_statistic') }}
                </span>
              </button>
            </div>
          </template>
          <template v-else>
            <TileSingle
              v-for="(element, index_element) in row?.elements"
              :key="element.id"
              v-model:element="
                localDashboard.analytic_rows[index_analytic_rows].elements[
                  index_element
                ]
              "
              v-model:filters="localDashboard.filters"
              :dashboard-row-id="
                localDashboard.analytic_rows[index_analytic_rows].id
              "
              :template-dashboard-id="
                localDashboard.analytic_rows[index_analytic_rows]
                  .template_dashboard_id
              "
              @save-dashboard="handleSaveDashboard"
              @add-after="() => addAfter(row.id, index_element)"
              @add-before="() => addAfter(row.id, index_element)"
              @delete="() => deleteElement(row.id, index_element)"
            />
          </template>
        </template>
        <template v-else>
          <template v-if="row.elements.length === 0">
            <div
              class="group relative col-span-1 flex h-72 flex-col items-center gap-2 rounded-md border p-4 pt-3"
            >
              <button
                class="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 text-lg font-medium"
                @click="() => add(row.id)"
              >
                <FontAwesomeIcon :icon="['fal', 'chart-bar']" size="3x" />
                <span class="">
                  {{ $t('global.click_hier_to_set_up_your_statistic') }}
                </span>
              </button>
            </div>
          </template>
          <template v-else>
            <TileCustom
              v-for="(element, index_element) in row?.elements"
              :key="element.id"
              v-model:element="
                localDashboard.analytic_rows[index_analytic_rows].elements[
                  index_element
                ]
              "
              v-model:filters="localDashboard.filters"
              :dashboard-row-id="
                localDashboard.analytic_rows[index_analytic_rows].id
              "
              :template-dashboard-id="
                localDashboard.analytic_rows[index_analytic_rows]
                  .template_dashboard_id
              "
              @save-dashboard="handleSaveDashboard"
              @add-after="() => addAfter(row.id, index_element)"
              @add-before="() => addBefore(row.id, index_element)"
              @delete="() => deleteElement(row.id, index_element)"
            />
          </template>
        </template>
      </div>
    </div>
  </template>
</template>

<script>
import TileSingle from './TileSingle.vue'
import TileCustom from './TileCustom.vue'
import { mapActions, mapGetters } from 'vuex'
import LoadingComponent from '@c/TheLoadingComponent.vue'
import DashboardHeader from '@c/Dashboard/Components/DashboardHeader.vue'
import DashboardPanelControl from '@c/Dashboard/Components/DashboardPanelControl.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
// import SortPlanningContent from '@c/Dashboard/Content/Planning/components/SortPlanningContent.vue'
import FilterLinesComponent from '@c/Dashboard/Components/FilterLinesComponent.vue'
import { upperFirst } from 'lodash-es'
import { datetime } from '@intlify/core-base'

const new_element = {
  simple: {
    title: {},
    format: 'compact',
    data: {}
  },
  large: {
    title: {},
    format: 'extended',
    data: {}
  }
}

export default {
  name: 'AnalyticsContent',
  components: {
    FilterLinesComponent,
    // SortPlanningContent,
    FontAwesomeIcon,
    DashboardPanelControl,
    DashboardHeader,
    TileSingle,
    TileCustom,
    LoadingComponent
  },
  props: {
    currentBoard: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      localDashboard: null,
      need_save_locked: true,
      requireSaveDashboard: false
    }
  },

  computed: {
    ...mapGetters({
      entries: 'funeralHome/getFuneralHomesList',
      auth: 'auth/getAuth',
      funeralHomes: 'metadata/getFuneralHomeMetadata',
      availableFilter: 'metadata/getAvailableFilterAnalytics'
    }),
    validFilter() {
      return (
        this.localDashboard.filters?.filter(
          (filter) => filter.column && filter.values && filter.condition
        ) ?? []
      )
    },
    renderedFilters() {
      return this.localDashboard.filters.map((filter, index) => {
        const conjunction =
          index !== 0
            ? filter.whereType === 'where'
              ? `&nbsp;${this.$t('global.and')}`
              : `&nbsp;${this.$t('global.or')}`
            : '&nbsp;'

        if (filter.column === 'death_date') {
          return (
            conjunction +
            '&nbsp' +
            this.upperFirst(
              this.$t('enums.analytics.statsPeriods.' + filter.values)
            ) +
            ''
          )
        }
        if (
          filter.column === 'funeral_home_id' &&
          filter.values &&
          this.entries.length
        ) {
          return (
            conjunction +
            '&nbsp' +
            this.filteredEntries(filter.values)[0]?.name +
            ''
          )
        }
        if (filter.column === 'date_specific') {
          return conjunction + ' ' + filter.values + ' '
        }
        if (filter.column === 'date_range') {
          return (
            conjunction +
            '&nbsp' +
            this.formattedDate(filter.values.start) +
            ' > ' +
            this.formattedDate(filter.values.end) +
            ''
          )
        }
        return ' '
      })
    }
  },
  watch: {
    currentBoard(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.fetchAnalyticsDashboard()
      }
    },
    localDashboard(newValue, oldValue) {
      if (newValue !== oldValue) {
      }
    }
  },
  mounted() {
    this.fetchAnalyticsDashboard()
    this.processFetchFuneralHomeListAction()
  },
  methods: {
    datetime,
    upperFirst,
    ...mapActions({
      processFetchTemplateDashboardByIdAction:
        'dashboard/fetchTemplateDashboardById',
      processUpdateTemplateDashboardByIdAction:
        'dashboard/updateTemplateDashboardById',
      processFetchFuneralHomeListAction: 'funeralHome/fetchFuneralHomesList'
    }),
    // fetchFuneralHome
    closePopover(close) {
      if (typeof close === 'function') {
        close()
      }
      // this.fetchEvents(this.currentStartDate, this.currentEndDate)
    },
    filteredEntries(funeralHomeId) {
      return this.entries.filter((entry) => entry['id'] === funeralHomeId)
    },
    formattedDate(isoDate) {
      const date = new Date(isoDate)

      return [
        String(date.getDate()).padStart(2, '0'), // Jour avec 2 chiffres
        String(date.getMonth() + 1).padStart(2, '0'), // Mois avec 2 chiffres
        date.getFullYear() // Année
      ].join('-')
    },
    handleSaveDashboard() {
      const requestData = {
        ...this.localDashboard
      }
      this.processUpdateTemplateDashboardByIdAction({
        id: this.localDashboard.id,
        data: requestData
      }).then((response) => {
        this.localDashboard.analytic_rows =
          response?.data?.template_dashboard.analytic_rows
        this.localDashboardOrdered(this.localDashboard)
      }).catch(() => {
        this.$h.toastError(this.$t('toasts.something_went_wrong'))
      })
    },
    fetchAnalyticsDashboard() {
      const targetId = this.currentBoard.id
      this.processFetchTemplateDashboardByIdAction({ id: targetId })
        .then((response) => {
          if (this.currentBoard.id !== targetId) return
          this.localDashboard = this.localDashboardOrdered(
            response.template_dashboard
          )
        })
        .catch((error) => {
          this.$router.push({ name: 'dashboard_main_page_route' })
        })
    },
    localDashboardOrdered(dashboard) {
      const template_dashboard = dashboard
      const elements = template_dashboard?.analytic_rows[1]?.elements

      if (elements?.length) {
        elements.forEach((element) => {
          const datasets = element?.data?.datasets

          if (datasets) {
            const data = datasets[0]?.data
            const combinedArray = element?.data?.labels
              ?.map((label, index) => ({
                label,
                value: data[0][Object.keys(data[0])[index]]
              }))
              .filter((item) => item.value)
              .sort((a, b) => b.value - a.value)

            datasets[0].data = combinedArray.map((item) => item.value)
            element.data.labels = combinedArray.map((item) => item.label)
          }
        })
      }

      return template_dashboard
    },
    dashboardChanged(_dashboard_template_id) {
      this.fetchAnalyticsDashboard()
    },
    add(row_id) {
      let row = this.localDashboard.analytic_rows.find(
        (row) => row.id === row_id
      )
      if (row && new_element[row.type]) {
        row.elements.push(new_element[row.type])
      }
    },
    addAfter(row_id, index_element) {
      let row = this.localDashboard.analytic_rows.find(
        (row) => row.id === row_id
      )
      if (row && new_element[row.type]) {
        row.elements.splice(index_element + 1, 0, new_element[row.type])
      }
    },
    addBefore(row_id, index_element) {
      let row = this.localDashboard.analytic_rows.find(
        (row) => row.id === row_id
      )
      if (row && new_element[row.type]) {
        row.elements.splice(index_element, 0, new_element[row.type])
      }
    },
    deleteElement(row_id, index_element) {
      this.localDashboard.analytic_rows
        .find((row) => row.id === row_id)
        .elements.splice(index_element, 1)
      this.handleSaveDashboard()
    }
  }
}
</script>

<style scoped></style>
